import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Grid, Icon, Divider, Chip, CircularProgress, Tooltip } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import Header from "layouts/profile/components/Header";
import ProfileUpdateModal from "layouts/profile/components/ProfileUpdateModal";

import api from "../../utils/axiosConfig";
const ROLE_LABELS = {
  ADMIN_GLOBAL: { label: "Administrateur Global", color: "#0A3475", bg: "#e8eef8" },
  ADMIN_INSTITUTION: { label: "Administrateur Institution", color: "#186A81", bg: "#e4f2f5" },
  UTILISATEUR: { label: "Utilisateur", color: "#4CAF50", bg: "#e8f5e9" },
};

function InfoRow({ icon, label, value }) {
  return (
    <MDBox
      display="flex"
      alignItems="center"
      py={1.4}
      px={1.5}
      sx={{
        borderRadius: "10px",
        transition: "background-color 0.2s",
        "&:hover": { backgroundColor: "rgba(24,106,129,0.06)" },
      }}
    >
      <MDBox
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{
          width: 38,
          height: 38,
          borderRadius: "10px",
          background: "linear-gradient(135deg, rgba(10,52,117,0.12), rgba(24,106,129,0.12))",
          mr: 2,
          flexShrink: 0,
        }}
      >
        <Icon sx={{ color: "#186A81", fontSize: "20px !important" }}>{icon}</Icon>
      </MDBox>
      <MDBox flex={1} minWidth={0}>
        <MDTypography variant="caption" color="text" sx={{ opacity: 0.65, display: "block" }}>
          {label}
        </MDTypography>
        <Tooltip title={value} placement="top-start">
          <MDTypography
            variant="button"
            fontWeight="medium"
            display="block"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
            }}
          >
            {value}
          </MDTypography>
        </Tooltip>
      </MDBox>
    </MDBox>
  );
}

InfoRow.propTypes = {
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
};

function Overview() {
  const [profileData, setProfileData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const handleProfileUpdateSuccess = () => {
    setIsModalOpen(false);
    setRefreshTrigger((prev) => prev + 1);
  };

  useEffect(() => {
    const fetchProfileData = async () => {
      setIsLoading(true);
      try {
        const response = await api.get("/api/auth/show-profile");
        setProfileData(response.data.profile);
      } catch (err) {
        setError("Impossible de charger les données du profil.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchProfileData();
  }, [refreshTrigger]);

  if (isLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" height="60vh">
          <CircularProgress sx={{ color: "#186A81" }} />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (error || !profileData) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" height="60vh">
          <MDTypography variant="h5" color="error">
            {error || "Aucune donnée de profil trouvée."}
          </MDTypography>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  const firstRoleDetail = profileData.roles_details?.[0];
  const institution = firstRoleDetail?.entite;
  const institutionName = institution?.nom || "Non rattaché";
  const entityType = firstRoleDetail?.type_entite || "N/A";
  const roleName = firstRoleDetail?.role_name || "N/A";
  const region = institution?.region || (institution ? "Non définie" : "Global");
  const roleStyle = ROLE_LABELS[roleName] || { label: roleName, color: "#7b809a", bg: "#f0f2f5" };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={2} />
      <Header name={profileData.name} role={roleStyle.label}>
        <MDBox mt={4} mb={3} px={1}>
          <Grid container spacing={3}>
            {/* ── Colonne gauche : informations ── */}
            <Grid item xs={12} md={7}>
              <MDBox
                sx={{
                  border: "1px solid rgba(0,0,0,0.06)",
                  borderRadius: "14px",
                  overflow: "hidden",
                }}
              >
                {/* En-tête section */}
                <MDBox
                  display="flex"
                  alignItems="center"
                  px={2.5}
                  py={2}
                  sx={{
                    background:
                      "linear-gradient(135deg, rgba(10,52,117,0.05), rgba(24,106,129,0.05))",
                    borderBottom: "1px solid rgba(0,0,0,0.06)",
                  }}
                >
                  <Icon sx={{ color: "#186A81", mr: 1, fontSize: "20px !important" }}>
                    person_outline
                  </Icon>
                  <MDTypography variant="h6" fontWeight="medium">
                    Informations personnelles
                  </MDTypography>
                </MDBox>

                <MDBox px={1.5} py={1}>
                  <InfoRow icon="email" label="Adresse email" value={profileData.email} />
                  <Divider sx={{ my: 0, opacity: 0.15 }} />
                  <InfoRow icon="business" label="Institution" value={institutionName} />
                  <Divider sx={{ my: 0, opacity: 0.15 }} />
                  <InfoRow icon="location_on" label="Région" value={region} />
                  <Divider sx={{ my: 0, opacity: 0.15 }} />
                  <InfoRow icon="category" label="Type d'entité" value={entityType} />
                  <Divider sx={{ my: 0, opacity: 0.15 }} />
                  <InfoRow icon="badge" label="Identifiant système" value={`#${profileData.id}`} />
                </MDBox>
              </MDBox>
            </Grid>

            {/* ── Colonne droite : statut + actions ── */}
            <Grid item xs={12} md={5}>
              <MDBox display="flex" flexDirection="column" gap={2} height="100%">
                {/* Statut & Rôle */}
                <MDBox
                  sx={{
                    border: "1px solid rgba(0,0,0,0.06)",
                    borderRadius: "14px",
                    overflow: "hidden",
                  }}
                >
                  <MDBox
                    display="flex"
                    alignItems="center"
                    px={2.5}
                    py={2}
                    sx={{
                      background:
                        "linear-gradient(135deg, rgba(10,52,117,0.05), rgba(24,106,129,0.05))",
                      borderBottom: "1px solid rgba(0,0,0,0.06)",
                    }}
                  >
                    <Icon sx={{ color: "#186A81", mr: 1, fontSize: "20px !important" }}>
                      verified_user
                    </Icon>
                    <MDTypography variant="h6" fontWeight="medium">
                      Statut & Rôle
                    </MDTypography>
                  </MDBox>
                  <MDBox px={2.5} py={2.5}>
                    <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <MDTypography variant="caption" color="text" sx={{ opacity: 0.65 }}>
                        Compte
                      </MDTypography>
                      <Chip
                        icon={
                          <Icon
                            sx={{
                              fontSize: "14px !important",
                              color: profileData.actif
                                ? "#2e7d32 !important"
                                : "#c62828 !important",
                            }}
                          >
                            {profileData.actif ? "check_circle" : "cancel"}
                          </Icon>
                        }
                        label={profileData.actif ? "Actif" : "Inactif"}
                        size="small"
                        sx={{
                          backgroundColor: profileData.actif ? "#e8f5e9" : "#ffebee",
                          color: profileData.actif ? "#2e7d32" : "#c62828",
                          fontWeight: "bold",
                          fontSize: "0.72rem",
                          height: 26,
                        }}
                      />
                    </MDBox>
                    <MDBox display="flex" alignItems="center" justifyContent="space-between">
                      <MDTypography variant="caption" color="text" sx={{ opacity: 0.65 }}>
                        Rôle
                      </MDTypography>
                      <Chip
                        label={roleStyle.label}
                        size="small"
                        sx={{
                          backgroundColor: roleStyle.bg,
                          color: roleStyle.color,
                          fontWeight: "bold",
                          fontSize: "0.72rem",
                          height: 26,
                        }}
                      />
                    </MDBox>
                  </MDBox>
                </MDBox>

                {/* À propos */}
                <MDBox
                  flex={1}
                  sx={{
                    border: "1px solid rgba(0,0,0,0.06)",
                    borderRadius: "14px",
                    overflow: "hidden",
                  }}
                >
                  <MDBox
                    display="flex"
                    alignItems="center"
                    px={2.5}
                    py={2}
                    sx={{
                      background:
                        "linear-gradient(135deg, rgba(10,52,117,0.05), rgba(24,106,129,0.05))",
                      borderBottom: "1px solid rgba(0,0,0,0.06)",
                    }}
                  >
                    <Icon sx={{ color: "#186A81", mr: 1, fontSize: "20px !important" }}>
                      info_outline
                    </Icon>
                    <MDTypography variant="h6" fontWeight="medium">
                      À propos
                    </MDTypography>
                  </MDBox>
                  <MDBox px={2.5} py={2.5}>
                    <MDTypography
                      variant="body2"
                      color="text"
                      sx={{ opacity: 0.75, lineHeight: 1.8, mb: 3 }}
                    >
                      {profileData.description ||
                        `Utilisateur ${roleStyle.label} sur la plateforme ConformIMF, en charge de la veille réglementaire pour les IMF de l'espace UEMOA.`}
                    </MDTypography>
                    <MDButton
                      variant="gradient"
                      color="info"
                      fullWidth
                      onClick={() => setIsModalOpen(true)}
                      sx={{ borderRadius: "10px" }}
                    >
                      <Icon sx={{ mr: 1 }}>edit</Icon>
                      Modifier le profil
                    </MDButton>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </Header>

      <Footer />

      <ProfileUpdateModal
        open={isModalOpen}
        onClose={handleProfileUpdateSuccess}
        profileData={profileData}
      />
    </DashboardLayout>
  );
}

export default Overview;
